/* eslint-disable */
import { useMemo, useState, useEffect } from 'react';
import { Modal, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Search, ImageOff } from 'lucide-react';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';

const SKU_TYPE_STYLES = {
	PRODUCT: 'bg-blue-100 text-blue-800',
	UNPACKED_PRODUCT: 'bg-indigo-100 text-indigo-800',
	INGREDIENT: 'bg-green-100 text-green-800',
	INTERMEDIATE: 'bg-purple-100 text-purple-800',
	PACKAGING: 'bg-orange-100 text-orange-800',
	OTHER: 'bg-slate-100 text-slate-700',
};

const flattenToVariantRows = (products, allowedSet) => {
	const rows = [];
	(products || []).forEach((product) => {
		if (allowedSet && !allowedSet.has(product?.skuType)) return;
		const variants = product?.variants?.length
			? product.variants
			: [{ id: `pv-${product?.id}`, displayName: '-', clientVariantId: '-' }];
		variants.forEach((variant) => {
			rows.push({
				id: `${product?.id}::${variant?.id}`,
				productId: product?.id,
				productName: product?.productName ?? '-',
				unit: product?.unit,
				productImageUrl: product?.productImageUrl,
				variantId: variant?.id,
				variantDisplayName: variant?.displayName || '-',
				clientVariantId: variant?.clientVariantId ?? '-',
				categoryName: product?.categoryName ?? '-',
				subCategoryName: product?.subCategoryName ?? '-',
				skuType: product?.skuType,
				_product: product,
				_variant: variant,
			});
		});
	});
	return rows;
};

const flattenToProductRows = (products, allowedSet) => {
	const rows = [];
	(products || []).forEach((product) => {
		if (allowedSet && !allowedSet.has(product?.skuType)) return;
		const defaultVariant = product?.variants?.find((v) => v?.isDefault)
			|| product?.variants?.[0]
			|| null;
		rows.push({
			id: `${product?.id}`,
			productId: product?.id,
			productName: product?.productName ?? '-',
			unit: product?.unit,
			productImageUrl: product?.productImageUrl,
			variantId: defaultVariant?.id ?? null,
			variantDisplayName: defaultVariant?.displayName || '-',
			clientVariantId: product?.clientVariantId
				?? defaultVariant?.clientVariantId
				?? '-',
			categoryName: product?.categoryName ?? '-',
			subCategoryName: product?.subCategoryName ?? '-',
			skuType: product?.skuType,
			_product: product,
			_variant: defaultVariant,
		});
	});
	return rows;
};

const AddProductSelectionModal = (props) => {
	const {
		show,
		mode = 'ingredient',
		list,
		products,
		skuType,
		allowedSkuTypes,
		existingIds = new Set(),
		heading = 'Add Recipe Sections — Select Variants',
		subtitle = '',
		confirmLabel = 'Add',
		onClose,
		onConfirm,
	} = props;

	const resolvedProducts = products && products.length ? products : (list || []);
	const resolvedAllowedTypes = skuType
		? (Array.isArray(skuType) ? skuType : [skuType])
		: allowedSkuTypes;

	const allowedSet = useMemo(
		() => (resolvedAllowedTypes ? new Set(resolvedAllowedTypes) : null),
		[resolvedAllowedTypes]
	);

	const [query, setQuery] = useState('');
	const [selectedRowIds, setSelectedRowIds] = useState(new Set());


	useEffect(() => {
		if (show) {
			setQuery('');
			setSelectedRowIds(new Set(existingIds));
		}
	}, [show, existingIds]);

	const allRows = useMemo(
		() => (mode === 'product'
			? flattenToProductRows(resolvedProducts, allowedSet)
			: flattenToVariantRows(resolvedProducts, allowedSet)),
		[resolvedProducts, allowedSet, mode]
	);

	const rows = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return allRows;
		return allRows.filter((r) =>
			r.productName.toLowerCase().includes(q) ||
			r.variantDisplayName.toLowerCase().includes(q) ||
			String(r.clientVariantId).toLowerCase().includes(q) ||
			r.categoryName.toLowerCase().includes(q) ||
			r.subCategoryName.toLowerCase().includes(q)
		);
	}, [allRows, query]);

	const allVisibleSelected = rows.length > 0 &&
		rows.every((r) => selectedRowIds.has(r.id));
	const someVisibleSelected = rows.some((r) => selectedRowIds.has(r.id)) &&
		!allVisibleSelected;

	const toggleOne = (rowId) => {
		setSelectedRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(rowId)) next.delete(rowId);
			else next.add(rowId);
			return next;
		});
	};

	const toggleAllVisible = () => {
		setSelectedRowIds((prev) => {
			const next = new Set(prev);
			if (allVisibleSelected) {
				rows.forEach((r) => next.delete(r.id));
			} else {
				rows.forEach((r) => next.add(r.id));
			}
			return next;
		});
	};

	const handleConfirm = () => {
		const added = allRows.filter(
			(r) => selectedRowIds.has(r.id) && !existingIds.has(r.id)
		);
		const removedRowIds = [...existingIds].filter((id) => !selectedRowIds.has(id));
		onConfirm({ added, removedRowIds });
	};

	const columns = useMemo(() => ([
		{
			key: 'select',
			title: 'Select',
			width: 60,
			customCol: true,
			noPadding: true,
		},
		{
			key: 'productNameVariant',
			title: mode === 'product' ? 'Product Name' : 'Product Name (Variant)',
			width: 400,
		},
		{
			key: 'skuCode',
			title: 'SKU Code',
			width: 170,
		},
		{
			key: 'category',
			title: 'Category',
			width: 170,
		},
		{
			key: 'subCategory',
			title: 'Subcategory',
			width: 170,
		},
	]), [mode]);

	const customRowData = useMemo(() => ({
		select: (row) => {
			const checked = selectedRowIds.has(row.id);
			return (
				<div className='w-full h-full flex items-center justify-center px-1'>
					<input
						type='checkbox'
						checked={checked}
						onChange={(e) => {
							e.stopPropagation();
							toggleOne(row.id);
						}}
						onClick={(e) => e.stopPropagation()}
						className='w-4 h-4 cursor-pointer'
						style={{ accentColor: '#4F46E5' }}
					/>
				</div>
			);
		},
		productNameVariant: (row) => {
			const isExisting = existingIds.has(row.id);
			const chipCls = SKU_TYPE_STYLES[row.skuType] || SKU_TYPE_STYLES.OTHER;
			return (
				<div className='flex items-center gap-3'>
					{row?.productImageUrl ? (
						<img
							src={row.productImageUrl}
							alt={row.productName}
							className='w-14 h-14 rounded-md object-cover border border-slate-200 shrink-0'
							onError={(e) => {
								e.currentTarget.style.display = 'none';
							}}
						/>
					) : (
						<div className='w-14 h-14 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 shrink-0'>
							<ImageOff className='size-5' />
						</div>
					)}
					<div className='min-w-0 flex-1'>
						<div className='text-slate-800 font-medium leading-tight'>
							{row.productName}
							{/* Only show the variant name in ingredient mode — in
							    product mode each row represents the whole product
							    and the variant column would be noise. */}
							{mode !== 'product' && (
								<>
									{' '}
									<span className='text-slate-500 font-normal'>
										({row.variantDisplayName})
									</span>
								</>
							)}
						</div>
						<div className='flex items-center gap-1.5 mt-1'>
							<span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${chipCls}`}>
								{row.skuType || 'UNKNOWN'}
							</span>
							{isExisting && (
								<span className='inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800'>
									Already added
								</span>
							)}
						</div>
					</div>
				</div>
			);
		},
		skuCode: (row) => (
			<div className='text-slate-700'>{row.clientVariantId}</div>
		),
		category: (row) => (
			<div className='text-slate-700'>{row.categoryName}</div>
		),
		subCategory: (row) => (
			<div className='text-slate-700'>{row.subCategoryName}</div>
		),
	}), [selectedRowIds, existingIds, rows, mode]);

	const renderCustomCol = (column) => {
		if (column.key === 'select') {
			return (
				<div className='w-full text-center justify-between'>
					<input
						type='checkbox'
						checked={allVisibleSelected}
						ref={(el) => {
							if (el) el.indeterminate = someVisibleSelected;
						}}
						onChange={toggleAllVisible}
						onClick={(e) => e.stopPropagation()}
						className='w-4 h-4 cursor-pointer'
						style={{ accentColor: '#4F46E5' }}
						disabled={rows.length === 0}
					/>
				</div>
			);
		}
		return column.title;
	};

	if (!show) return null;

	const selectedCount = selectedRowIds.size;

	return (
		<Modal
			open={show}
			onClose={(e, reason) => {
				if (reason === 'backdropClick') return;
				onClose();
			}}
			aria-labelledby='add-product-selection-modal'
		>
			<div
				className='absolute top-1/2 left-1/2 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden focus:outline-none'
				style={{
					width: '75rem',
					maxWidth: '95vw',
					height: '85vh',
					transform: 'translate(-50%, -50%)',
				}}
			>
				{/* Header */}
				<div className='shrink-0 flex justify-between items-center px-6 py-3 border-b border-slate-200 bg-white gap-4'>
					<div className='min-w-0'>
						<h3 className='text-lg font-bold text-slate-900 truncate'>
							{heading}
						</h3>
						{subtitle && (
							<p className='text-xs text-slate-500 mt-0.5'>{subtitle}</p>
						)}
					</div>
					<IconButton onClick={onClose} size='small'>
						<Close className='text-slate-500' />
					</IconButton>
				</div>

				{/* Search bar + inline counter on the right */}
				<div className='shrink-0 px-6 py-3 border-b border-slate-200 bg-slate-50'>
					<div className='flex items-center gap-3'>
						<div className='relative flex-1 min-w-0'>
							<Search className='size-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
							<input
								type='text'
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder='Search by product, variant, SKU code, category…'
								className='w-full pl-10 pr-3 py-2 text-[13px] border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500'
							/>
						</div>
						<div className='text-[12px] text-slate-500 shrink-0 whitespace-nowrap'>
							{selectedCount > 0
								? `${selectedCount} selected · ${rows.length} matching`
								: `${rows.length} items`}
						</div>
					</div>
				</div>

				{/* Virtualized table */}
				<div className='flex-1 bg-white overflow-hidden'>
					<VariantTable
						data={rows}
						columns={columns}
						colMapping={{
							select: 'id',
							productNameVariant: 'productName',
							skuCode: 'clientVariantId',
							category: 'categoryName',
							subCategory: 'subCategoryName',
						}}
						customRowData={customRowData}
						customCol={renderCustomCol}
						virtualization
						headerCellClassName='!bg-indigo-50 !text-indigo-900'
						headerRowClassName='!bg-indigo-50'
						customInfoLine={
							query
								? `No items match "${query}".`
								: 'No items available.'
						}
					/>
				</div>

				{/* Footer */}
				<div className='shrink-0 flex justify-end items-center gap-2 px-6 py-3 border-t-2 border-slate-200 bg-white'>
					<button
						type='button'
						onClick={onClose}
						className='px-4 py-2 text-[13px] font-semibold rounded-md bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 transition-colors'
					>
						Cancel
					</button>
					<button
						type='button'
						onClick={handleConfirm}
						disabled={selectedCount === 0}
						className={
							'px-6 py-2 text-[13px] font-bold rounded-md transition-colors ' +
							(selectedCount === 0
								? 'bg-slate-200 text-slate-400 cursor-not-allowed'
								: 'bg-indigo-600 hover:bg-indigo-700 text-white')
						}
					>
						{confirmLabel} {selectedCount > 0 ? `(${selectedCount})` : ''}
					</button>
				</div>
			</div>
		</Modal>
	);
};

export default AddProductSelectionModal;
